import * as React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth, hasRole, can, hasModule, adminPortal, type Role } from '@/store/auth';
import { Toaster } from '@/components/Toaster';
import { BrandingApplier } from '@/lib/branding';
import { PwaBanners } from '@/lib/pwa';
import { AdminLayout, firstPage } from '@/components/layout/AdminLayout';
import Login, { homeFor } from '@/pages/Login';
import Dashboard from '@/pages/admin/Dashboard';
import { AgentsPage, CampaignsPage, ContactListsPage, ContactsPage, DispositionsPage, CarriersPage, TrunksPage, InboundTrunksPage, RoutingPage,
  InboundPage, IvrPage, QueuesPage, BroadcastPage } from '@/pages/admin/Config';
import { DialerPage, DncPage, CallbacksPage } from '@/pages/admin/Operations';
import { CdrPage, RecordingsPage, ReportsPage } from '@/pages/admin/Records';
import { AudioLibraryPage } from '@/pages/admin/AudioLibrary';
import { UsersPage, SettingsPage, AuditLogsPage } from '@/pages/admin/Admin';
import { TenantsPage } from '@/pages/admin/Platform';
import { PermissionGroupsPage } from '@/pages/admin/Permissions';
import { BrandingPage } from '@/pages/admin/Branding';
import { NotificationsPage } from '@/pages/admin/Notifications';
import { CrmPage } from '@/pages/admin/crm/CrmPage';
import { ConnectionPage } from '@/pages/admin/crm/ConnectionPage';
import { agentRoutes } from '@/pages/agent/routes';
import { ExtensionsPage, PhonesPage, RingGroupsPage, PickupGroupsPage, PagingGroupsPage, ParkLotsPage, ConferencesPage, VoicemailBoxesPage,
  ClassOfServicePage, FeatureCodesPage, SpeedDialPage, PbxStatusPage, SipBansPage } from '@/pages/admin/Pbx';
import { MyPhonePage, UserLayout } from '@/pages/me/MyPhone';
import { t } from '@/i18n';

// admin: also agents and phone users in a permission group (2026-10-15, the supervisor user type is retired)
function RequireRole({ roles, admin, children }: { roles: Role[]; admin?: boolean; children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const loc = useLocation();
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (!hasRole(user.role, roles) && !(admin && adminPortal(user))) return <Navigate to={homeFor(user)} replace />;
  return <>{children}</>;
}

// a page needs one of these rights (PHASE23); without it, the first page the user may open
function RequireRight({ need, children }: { need: string[]; children: React.ReactNode }) {
  const auth = useAuth();
  if (!auth.permissions && !hasRole(auth.user?.role, ['ADMIN'])) return null;      // rights still loading
  if (!can(auth, ...need)) {
    const to = firstPage(auth);
    return to ? <Navigate to={to} replace /> : <p className="text-sm text-muted-foreground" data-testid="no-access">{t('Your account has no pages to open. Ask an administrator.')}</p>;
  }
  return <>{children}</>;
}
const R = (need: string, el: React.ReactNode) => <RequireRight need={need.split('|')}>{el}</RequireRight>;

// the dashboard of a tenant without the call center is the PBX status page
function Home() {
  const tenant = useAuth((s) => s.tenant);
  if (tenant && !hasModule(tenant, 'CALLCENTER') && hasModule(tenant, 'PBX')) return R('extensions.view', <PbxStatusPage />);
  return R('dashboard.view', <Dashboard />);
}

export default function App() {
  const user = useAuth((s) => s.user);
  return (
    <>
      <PwaBanners />
      <Routes>
        <Route path="/login" element={user ? <Navigate to={homeFor(user)} replace /> : <Login />} />
        <Route path="/admin" element={<RequireRole roles={['ADMIN', 'SUPERVISOR']} admin><AdminLayout /></RequireRole>}>
          <Route index element={<Home />} />
          <Route path="agents" element={R('agents.view', <AgentsPage />)} />
          <Route path="campaigns" element={R('campaigns.view', <CampaignsPage />)} />
          <Route path="contact-lists" element={R('contacts.view', <ContactListsPage />)} />
          <Route path="contacts" element={R('contacts.view', <ContactsPage />)} />
          <Route path="dialer" element={R('campaigns.view', <DialerPage />)} />
          <Route path="queues" element={R('queues.view', <QueuesPage />)} />
          <Route path="inbound" element={R('inbound.view', <InboundPage />)} />
          <Route path="ivr" element={R('ivr.view', <IvrPage />)} />
          <Route path="broadcast" element={R('broadcasts.view', <BroadcastPage />)} />
          <Route path="trunks" element={R('routing.view', <TrunksPage />)} />
          <Route path="inbound-trunks" element={R('routing.view', <InboundTrunksPage />)} />
          <Route path="carriers" element={R('routing.view', <CarriersPage />)} />
          <Route path="routing" element={R('routing.view', <RoutingPage />)} />
          <Route path="dnc" element={R('dnc.view', <DncPage />)} />
          <Route path="callbacks" element={R('callbacks.view', <CallbacksPage />)} />
          <Route path="dispositions" element={R('dispositions.view', <DispositionsPage />)} />
          <Route path="cdr" element={R('cdr.view', <CdrPage />)} />
          <Route path="recordings" element={R('recordings.view', <RecordingsPage />)} />
          <Route path="audio-library" element={R('audio.view', <AudioLibraryPage />)} />
          <Route path="reports" element={R('reports.view', <ReportsPage />)} />
          <Route path="users" element={R('users.view', <UsersPage />)} />
          <Route path="permission-groups" element={R('permission_groups.view', <PermissionGroupsPage />)} />
          <Route path="branding" element={R('branding.view', <BrandingPage />)} />
          <Route path="notifications" element={R('notifications.view', <NotificationsPage />)} />
          <Route path="crm" element={R('crm.view', <CrmPage />)} />
          <Route path="crm/:id" element={R('crm.view', <ConnectionPage />)} />
          <Route path="settings" element={R('settings.view', <SettingsPage />)} />
          <Route path="audit-logs" element={R('audit.view', <AuditLogsPage />)} />
          <Route path="tenants" element={<RequireRole roles={['PLATFORM']}><TenantsPage /></RequireRole>} />
          {/* IP PBX (docs/PBX.md) */}
          <Route path="pbx" element={R('extensions.view', <PbxStatusPage />)} />
          <Route path="extensions" element={R('extensions.view', <ExtensionsPage />)} />
          <Route path="phones" element={R('extensions.view', <PhonesPage />)} />
          <Route path="ring-groups" element={R('pbx_groups.view', <RingGroupsPage />)} />
          <Route path="pickup-groups" element={R('pbx_groups.view', <PickupGroupsPage />)} />
          <Route path="paging-groups" element={R('pbx_groups.view', <PagingGroupsPage />)} />
          <Route path="park" element={R('pbx_groups.view', <ParkLotsPage />)} />
          <Route path="conferences" element={R('conferences.view', <ConferencesPage />)} />
          <Route path="voicemail-boxes" element={R('voicemail_boxes.view', <VoicemailBoxesPage />)} />
          <Route path="class-of-service" element={R('pbx_settings.view', <ClassOfServicePage />)} />
          <Route path="feature-codes" element={R('pbx_settings.view', <FeatureCodesPage />)} />
          <Route path="speed-dial" element={R('pbx_settings.view', <SpeedDialPage />)} />
          <Route path="sip-security" element={<RequireRole roles={['PLATFORM']}><SipBansPage /></RequireRole>} />
          <Route path="my-phone" element={<MyPhonePage />} />
        </Route>
        <Route path="/agent" element={<RequireRole roles={['AGENT']}>{agentRoutes.layout}</RequireRole>}>{agentRoutes.children}</Route>
        <Route path="/me" element={<RequireRole roles={['USER']}><UserLayout /></RequireRole>} />
        <Route path="*" element={<Navigate to={user ? homeFor(user) : '/login'} replace />} />
      </Routes>
      <Toaster />
      <BrandingApplier />
    </>
  );
}
